/**
 * Role-name helpers used across the admin app.
 *
 * Keycloak realm roles come in two forms depending on the API :
 * - Spring Security side (`/me`, `/api/users`) → prefixed `ROLE_ADMIN`
 * - Keycloak JWT side (`realm_access.roles`) → bare `ADMIN`
 *
 * We always work with bare names in the UI. Guards, directives and tests
 * use `roleGuard('ADMIN')` / `*hasRole="'ADMIN'"` regardless of the source.
 */

/** Removes the Spring Security `ROLE_` prefix if present. */
export function stripRolePrefix(role: string): string {
  return role.startsWith('ROLE_') ? role.substring(5) : role;
}

/**
 * Business roles are UPPERCASE and not `DEFAULT-*` — the rest are Keycloak
 * technical roles (`offline_access`, `uma_authorization`, `default-roles-*`).
 */
export function isBusinessRole(name: string): boolean {
  const n = stripRolePrefix(name);
  return n === n.toUpperCase() && !n.toUpperCase().startsWith('DEFAULT-');
}

/**
 * Returns the business-role subset of a list, stripped of any `ROLE_` prefix.
 * Convenient for badge displays.
 */
export function filterBusinessRoles(
  roles: readonly string[] | null | undefined,
): string[] {
  return (roles ?? []).map(stripRolePrefix).filter(isBusinessRole);
}

/**
 * Sort comparator for role lists : business roles first, then alphabetical.
 * Use as `roles.sort(byBusinessThenAlpha)`.
 */
export function byBusinessThenAlpha(
  a: { name: string },
  b: { name: string },
): number {
  const aBiz = isBusinessRole(a.name);
  const bBiz = isBusinessRole(b.name);
  if (aBiz !== bBiz) return aBiz ? -1 : 1;
  return a.name.localeCompare(b.name);
}
